import { useEffect, useRef, useState } from 'react';

const Hero = () => {
  const heroRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // ─── VIDEO SOURCES ────────────────────────────────────────────────────────
  // Replace these with your own hosted MP4 URLs (Cloudinary, S3, etc.)
  const slides = [
    {
      video: '/video1.mp4',
      tagline: 'Excellence in Action',
      title: 'Crafting Iconic Spaces',
      subtitle: 'with World-Class Quality',
      desc: "Purvanchal Global Ventures' penchant for perfection is reflected in every project, every decision and every innovation — delivering luxury residential & commercial spaces across India.",
    },
    {
      video: '/video2.mp4',
      tagline: 'New Launch',
      title: 'PGV Rivana',
      subtitle: 'Greater Noida',
      desc: 'Ultra-luxury residences at Sector 1, Greater Noida. Sprawling apartments with world-class amenities and unmatched craftsmanship.',
    },
    {
      video: '/video1.mp4',
      tagline: 'Featured Project',
      title: 'PGV Altair',
      subtitle: 'Bengaluru',
      desc: 'Redefining the skyline of Bengaluru. Premium residences that blend architectural brilliance with nature-inspired design.',
    },
  ];

  const stats = [
    { number: '29+', label: 'Years' },
    { number: '14', label: 'Cities' },
    { number: '400+', label: 'Projects' },
    { number: '100K+', label: 'Homes' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 30000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Close menus on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen || searchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, searchOpen]);

  return (
    <>
      {/* ── Global Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

        * { box-sizing: border-box; }

        .pgv-hero {
          font-family: 'Montserrat', sans-serif;
          position: relative;
          width: 100%;
          height: 100vh;
          min-height: 680px;
          overflow: hidden;
          background: #0a0a0a;
        }

        /* ── Video Background ── */
        .pgv-video-container {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .pgv-video-container video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.75;
        }

        /* ── Overlays ── */
        .pgv-overlay-main {
          position: absolute; inset: 0; z-index: 1;
          background: linear-gradient(
            to bottom,
            rgba(0,0,0,0.5) 0%,
            rgba(0,0,0,0.15) 35%,
            rgba(0,0,0,0.15) 60%,
            rgba(0,0,0,0.75) 100%
          );
        }
        .pgv-overlay-left {
          position: absolute; inset: 0; z-index: 1;
          background: linear-gradient(to right, rgba(0,0,0,0.55) 0%, transparent 55%);
        }

        /* ── Mobile Menu Toggle (Only visible on mobile, since header is removed) ── */
        .pgv-mobile-toggle {
          position: absolute;
          top: 20px;
          right: 20px;
          z-index: 30;
          display: none;
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 40px;
          padding: 12px 16px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .pgv-mobile-toggle:hover {
          background: rgba(0,0,0,0.7);
          border-color: rgba(201,168,76,0.5);
        }
        .pgv-mobile-toggle span {
          display: block;
          width: 22px;
          height: 2px;
          background: rgba(255,255,255,0.85);
          margin: 4px 0;
          transition: all 0.3s ease;
        }
        .pgv-mobile-toggle:hover span {
          background: #c9a84c;
        }

        /* ── Hero Content ── */
        .pgv-content {
          position: absolute;
          z-index: 10;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 0 clamp(20px, 5vw, 60px) clamp(40px, 8vh, 80px);
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 30px;
        }

        .pgv-text-block {
          max-width: 580px;
          animation: pgvFadeUp 1s ease 0.4s both;
        }

        .pgv-eyebrow {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .pgv-eyebrow-line {
          width: 36px;
          height: 1px;
          background: #c9a84c;
        }
        .pgv-eyebrow-text {
          font-size: clamp(9px, 2vw, 10px);
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #c9a84c;
        }

        .pgv-hero-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(32px, 5vw, 60px);
          font-weight: 300;
          line-height: 1.15;
          color: #fff;
          margin-bottom: 6px;
        }
        .pgv-hero-subtitle {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(28px, 4.5vw, 54px);
          font-weight: 400;
          font-style: italic;
          color: #c9a84c;
          margin-bottom: 20px;
          line-height: 1.15;
        }

        .pgv-hero-desc {
          font-size: clamp(12px, 1.8vw, 14px);
          font-weight: 300;
          line-height: 1.7;
          color: rgba(255,255,255,0.72);
          max-width: 480px;
          margin-bottom: 36px;
          letter-spacing: 0.02em;
        }

        .pgv-btn-row {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .pgv-btn-gold {
          background: #c9a84c;
          color: #fff;
          border: none;
          padding: 12px 28px;
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(9px, 1.8vw, 10px);
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.25s, transform 0.2s;
          position: relative;
          overflow: hidden;
        }
        .pgv-btn-gold::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.15);
          opacity: 0;
          transition: opacity 0.2s;
        }
        .pgv-btn-gold:hover { background: #b8933e; transform: translateY(-1px); }
        .pgv-btn-gold:hover::after { opacity: 1; }

        .pgv-btn-ghost {
          background: transparent;
          color: rgba(255,255,255,0.85);
          border: 1px solid rgba(255,255,255,0.4);
          padding: 12px 28px;
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(9px, 1.8vw, 10px);
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          transition: border-color 0.25s, color 0.25s, transform 0.2s;
        }
        .pgv-btn-ghost:hover {
          border-color: #c9a84c;
          color: #c9a84c;
          transform: translateY(-1px);
        }

        /* ── Stats Panel ── */
        .pgv-stats-panel {
          display: flex;
          background: rgba(0,0,0,0.35);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.1);
          border-bottom: none;
          border-right: none;
          animation: pgvFadeUp 1s ease 0.6s both;
          align-self: flex-end;
          flex-wrap: wrap;
        }
        .pgv-stat-cell {
          padding: clamp(15px, 3vh, 20px) clamp(20px, 4vw, 30px);
          border-right: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          min-width: 80px;
          cursor: default;
          transition: background 0.25s;
        }
        .pgv-stat-cell:last-child { border-right: none; }
        .pgv-stat-cell:hover { background: rgba(201,168,76,0.1); }
        .pgv-stat-number {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(22px, 4vw, 26px);
          font-weight: 400;
          color: #c9a84c;
          line-height: 1;
          display: block;
        }
        .pgv-stat-name {
          font-size: clamp(8px, 1.8vw, 9px);
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.55);
          display: block;
          margin-top: 6px;
        }

        /* ── Slide Dots ── */
        .pgv-dots {
          position: absolute;
          bottom: clamp(16px, 3vh, 32px);
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .pgv-dot-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
        }
        .pgv-dot-inner {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.3);
          transition: background 0.3s, transform 0.3s;
        }
        .pgv-dot-btn.active .pgv-dot-inner {
          background: #c9a84c;
          transform: scale(1.4);
        }

        /* ── Slide Counter ── */
        .pgv-counter {
          position: absolute;
          right: clamp(16px, 4vw, 44px);
          bottom: clamp(20px, 4vh, 36px);
          z-index: 10;
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(11px, 2vw, 13px);
          color: rgba(255,255,255,0.5);
          letter-spacing: 0.1em;
        }
        .pgv-counter-current {
          color: #c9a84c;
          font-size: clamp(14px, 2.5vw, 18px);
        }

        /* ── Vertical Scroll Hint (Hidden on mobile) ── */
        .pgv-scroll-hint {
          position: absolute;
          right: 30px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .pgv-scroll-text {
          font-size: 9px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          writing-mode: vertical-rl;
        }
        .pgv-scroll-line {
          width: 1px;
          height: 48px;
          background: linear-gradient(to bottom, rgba(201,168,76,0.8), rgba(201,168,76,0));
          animation: pgvScrollLine 2s ease-in-out infinite;
        }

        /* ── Progress bar ── */
        .pgv-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 2px;
          background: #c9a84c;
          z-index: 10;
          animation: pgvProgressBar 6s linear infinite;
        }

        /* ── Mobile Menu Overlay ── */
        .pgv-mobile-menu {
          position: fixed;
          inset: 0;
          z-index: 100;
          background: rgba(10,10,10,0.98);
          backdrop-filter: blur(20px);
          display: flex;
          flex-direction: column;
          padding: clamp(24px, 5vh, 40px);
          animation: pgvMenuSlide 0.4s ease both;
        }
        .pgv-mobile-menu-header {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          margin-bottom: 48px;
        }
        .pgv-close-btn {
          background: rgba(255,255,255,0.1);
          border: none;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: rgba(255,255,255,0.8);
          font-size: 24px;
          transition: all 0.2s;
        }
        .pgv-close-btn:hover {
          background: rgba(201,168,76,0.2);
          color: #c9a84c;
        }
        .pgv-mobile-nav-links {
          list-style: none;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .pgv-mobile-nav-links li {
          border-bottom: 1px solid rgba(255,255,255,0.07);
          padding: 16px 0;
        }
        .pgv-mobile-nav-links a {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(28px, 6vw, 42px);
          font-weight: 300;
          color: rgba(255,255,255,0.85);
          text-decoration: none;
          letter-spacing: 0.04em;
          transition: color 0.2s;
          display: block;
        }
        .pgv-mobile-nav-links a:hover { color: #c9a84c; }

        /* ── Search Overlay ── */
        .pgv-search-overlay {
          position: fixed;
          inset: 0;
          z-index: 100;
          background: rgba(10,10,10,0.96);
          backdrop-filter: blur(20px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: pgvMenuSlide 0.3s ease both;
          padding: 20px;
        }
        .pgv-search-label {
          font-size: clamp(9px, 2vw, 10px);
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 24px;
        }
        .pgv-search-input-wrap {
          display: flex;
          align-items: center;
          border-bottom: 1px solid rgba(255,255,255,0.3);
          width: min(500px, 90%);
          padding-bottom: 12px;
          gap: 12px;
        }
        .pgv-search-input {
          flex: 1;
          background: none;
          border: none;
          outline: none;
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(20px, 5vw, 28px);
          font-weight: 300;
          color: #fff;
          letter-spacing: 0.04em;
        }
        .pgv-search-input::placeholder {
          color: rgba(255,255,255,0.25);
          font-size: clamp(16px, 4vw, 22px);
        }
        .pgv-search-icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #c9a84c;
          font-size: 20px;
          padding: 8px;
        }
        .pgv-search-hint {
          margin-top: 20px;
          font-size: clamp(10px, 2vw, 11px);
          color: rgba(255,255,255,0.3);
          letter-spacing: 0.1em;
          text-align: center;
        }

        /* ── Keyframes ── */
        @keyframes pgvFadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pgvScrollLine {
          0%   { opacity: 0; transform: scaleY(0); transform-origin: top; }
          50%  { opacity: 1; transform: scaleY(1); transform-origin: top; }
          100% { opacity: 0; transform: scaleY(1); transform-origin: bottom; }
        }
        @keyframes pgvProgressBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes pgvMenuSlide {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Content transition ── */
        .pgv-slide-content {
          animation: pgvFadeUp 0.7s ease both;
        }

        /* ── Responsive Breakpoints ── */
        @media (max-width: 1024px) {
          .pgv-content {
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
          }
          .pgv-stats-panel {
            align-self: flex-start;
          }
          .pgv-scroll-hint {
            display: none;
          }
          .pgv-mobile-toggle {
            display: flex;
            flex-direction: column;
          }
        }

        @media (max-width: 768px) {
          .pgv-hero {
            min-height: 600px;
          }
          .pgv-content {
            padding-bottom: 60px;
          }
          .pgv-text-block {
            max-width: 100%;
          }
          .pgv-stats-panel {
            width: 100%;
            justify-content: space-between;
          }
          .pgv-stat-cell {
            flex: 1;
            padding: 12px 8px;
            min-width: auto;
          }
          .pgv-stat-number {
            font-size: 20px;
          }
          .pgv-stat-name {
            font-size: 7px;
            letter-spacing: 0.12em;
          }
          .pgv-btn-row {
            gap: 12px;
          }
          .pgv-btn-gold, .pgv-btn-ghost {
            padding: 10px 20px;
          }
        }

        @media (max-width: 480px) {
          .pgv-hero {
            min-height: 550px;
          }
          .pgv-content {
            padding: 0 16px 50px;
          }
          .pgv-eyebrow {
            margin-bottom: 10px;
          }
          .pgv-hero-desc {
            margin-bottom: 24px;
          }
          .pgv-stats-panel {
            flex-wrap: wrap;
          }
          .pgv-stat-cell {
            min-width: 70px;
            padding: 10px 6px;
          }
          .pgv-stat-number {
            font-size: 18px;
          }
          .pgv-dots {
            bottom: 16px;
          }
          .pgv-counter {
            bottom: 20px;
            right: 16px;
          }
          .pgv-mobile-nav-links li {
            padding: 12px 0;
          }
          .pgv-mobile-nav-links a {
            font-size: 28px;
          }
        }

        @media (max-width: 380px) {
          .pgv-stat-name {
            font-size: 6px;
            letter-spacing: 0.08em;
          }
          .pgv-btn-gold, .pgv-btn-ghost {
            padding: 8px 16px;
            font-size: 8px;
          }
        }

        /* Landscape mode on mobile */
        @media (max-width: 900px) and (orientation: landscape) {
          .pgv-hero {
            min-height: 500px;
          }
          .pgv-content {
            padding-bottom: 40px;
          }
          .pgv-text-block {
            max-width: 50%;
          }
          .pgv-hero-desc {
            font-size: 11px;
            margin-bottom: 16px;
          }
          .pgv-stats-panel {
            align-self: flex-end;
          }
        }
      `}</style>

      <section className="pgv-hero" ref={heroRef}>
        {/* ── Video Background ── */}
        <div className="pgv-video-container">
          <video
            key={slides[activeSlide].video}
            autoPlay
            muted
            loop
            playsInline
            poster="https://images.unsplash.com/photo-1545324412-359d64eebf5a?w=1400&q=80"
          >
            <source src={slides[activeSlide].video} type="video/mp4" />
          </video>
        </div>

        {/* ── Overlays ── */}
        <div className="pgv-overlay-main" />
        <div className="pgv-overlay-left" />

        {/* ── Progress Bar ── */}
        <div key={activeSlide} className="pgv-progress" />

        {/* ── Mobile Menu Toggle (Only visible on mobile/tablet) ── */}
        {/* <button
          className="pgv-mobile-toggle"
          onClick={() => setMenuOpen(true)}
          aria-label="Open Menu"
        >
          <span />
          <span />
          <span />
        </button> */}

        {/* ── Hero Content ── */}
        <div className="pgv-content">
          <div className="pgv-text-block pgv-slide-content" key={`text-${activeSlide}`}>
            {/* Eyebrow */}
            <div className="pgv-eyebrow">
              <div className="pgv-eyebrow-line" />
              <span className="pgv-eyebrow-text">{slides[activeSlide].tagline}</span>
            </div>

            {/* Title */}
            <h1 className="pgv-hero-title">{slides[activeSlide].title}</h1>
            <p className="pgv-hero-subtitle">{slides[activeSlide].subtitle}</p>
            <p className="pgv-hero-desc">{slides[activeSlide].desc}</p>

            {/* CTAs */}
            <div className="pgv-btn-row">
              <a href="#projects" className="pgv-btn-gold">Explore Projects</a>
              <a href="#contact" className="pgv-btn-ghost">View All Locations</a>
            </div>
          </div>

          {/* Stats panel */}
          <div className="pgv-stats-panel">
            {stats.map((stat) => (
              <div key={stat.label} className="pgv-stat-cell">
                <span className="pgv-stat-number">{stat.number}</span>
                <span className="pgv-stat-name">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Slide Dots ── */}
        <div className="pgv-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`pgv-dot-btn ${i === activeSlide ? 'active' : ''}`}
              onClick={() => setActiveSlide(i)}
              aria-label={`Slide ${i + 1}`}
            >
              <div className="pgv-dot-inner" />
            </button>
          ))}
        </div>

        {/* ── Slide Counter ── */}
        <div className="pgv-counter">
          <span className="pgv-counter-current">0{activeSlide + 1}</span>
          {' '}/ 0{slides.length}
        </div>

        {/* ── Scroll hint (Hidden on mobile) ── */}
        <div className="pgv-scroll-hint">
          <span className="pgv-scroll-text">Scroll</span>
          <div className="pgv-scroll-line" />
        </div>

        {/* ── Mobile Menu Overlay ── */}
        {/* {menuOpen && (
          <div className="pgv-mobile-menu">
            <div className="pgv-mobile-menu-header">
              <button
                className="pgv-close-btn"
                onClick={() => setMenuOpen(false)}
                aria-label="Close Menu"
              >
                ✕
              </button>
            </div>
            <ul className="pgv-mobile-nav-links">
              <li><a href="#about" onClick={() => setMenuOpen(false)}>Who We Are</a></li>
              <li><a href="#projects" onClick={() => setMenuOpen(false)}>What We Do</a></li>
              <li><a href="#contact" onClick={() => setMenuOpen(false)}>Contact Us</a></li>
              <li><a href="#" onClick={() => setMenuOpen(false)}>Blog</a></li>
              <li><a href="#" onClick={() => setMenuOpen(false)}>Careers</a></li>
              <li><a href="#" onClick={() => setMenuOpen(false)}>Media Centre</a></li>
              <li><a href="#" onClick={() => setMenuOpen(false)}>Sustainability</a></li>
              <li><a href="#" onClick={() => setMenuOpen(false)}>Investor Relations</a></li>
              <li><a href="#" onClick={() => setMenuOpen(false)}>PGV Privilege</a></li>
            </ul>
          </div>
        )}

        {/* ── Search Overlay ── */}
        {searchOpen && (
          <div className="pgv-search-overlay">
            <button
              className="pgv-close-btn"
              onClick={() => setSearchOpen(false)}
              style={{ position: 'absolute', top: 'clamp(20px, 5vh, 32px)', right: 'clamp(20px, 5vw, 40px)' }}
              aria-label="Close Search"
            >
              ✕
            </button>
            <div className="pgv-search-label">Search Properties</div>
            <div className="pgv-search-input-wrap">
              <input
                className="pgv-search-input"
                placeholder="City, project or locality..."
                autoFocus
              />
              <button className="pgv-search-icon-btn" aria-label="Search">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </button>
            </div>
            <p className="pgv-search-hint">Try "Bengaluru", "PGV Altair" or "3 BHK Gurugram"</p>
          </div>
        )}
      </section>
    </>
  );
};

export default Hero;